import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { hostname } from "../utils";

// AudioWorklet processor code as a string - will be loaded as a Blob URL
const AUDIO_PROCESSOR_CODE = `
const MAX_16BIT_INT = 32767;
class AudioProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const float32Array = Float32Array.from(input[0]);
    const int16Array = Int16Array.from(float32Array.map((n) => n * MAX_16BIT_INT));
    this.port.postMessage({ audio_data: int16Array.buffer });
    return true;
  }
}
registerProcessor("audio-processor", AudioProcessor);
`;

interface MicrophoneController {
  requestPermission: () => Promise<void>;
  startRecording: (onAudioCallback: (chunk: Uint8Array) => void) => Promise<void>;
  stopRecording: () => void;
  getStream: () => MediaStream | null;
}

type DictationProvider = "assemblyai" | "deepgram";

interface UseDictationOptions {
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  onError?: (error: string) => void;
  provider?: DictationProvider;
  /** Timeout in ms to stop dictation if no speech detected. Set to 0 to disable. Default: 20000 */
  inactivityTimeout?: number;
}

interface UseDictationReturn {
  isRecording: boolean;
  isLoading: boolean;
  transcript: string;
  startDictation: () => Promise<void>;
  stopDictation: () => void;
  error: string | null;
}

/**
 * Merge two Int16Array buffers into one
 */
function mergeBuffers(lhs: Int16Array, rhs: Int16Array): Int16Array {
  const merged = new Int16Array(lhs.length + rhs.length);
  merged.set(lhs, 0);
  merged.set(rhs, lhs.length);
  return merged;
}

/**
 * Create a microphone controller for capturing audio
 */
function createMicrophone(): MicrophoneController {
  let stream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let audioWorkletNode: AudioWorkletNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let audioBufferQueue = new Int16Array(0);
  let processorBlobUrl: string | null = null;
  let isAborted = false;

  return {
    async requestPermission() {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    },

    async startRecording(onAudioCallback: (chunk: Uint8Array) => void) {
      // Check if we were aborted before starting
      if (isAborted) {
        return;
      }

      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        // Check again after async operation
        if (isAborted) {
          stream.getTracks().forEach((track) => track.stop());
          stream = null;
          return;
        }
      }

      audioContext = new AudioContext({
        sampleRate: 16000,
        latencyHint: "balanced",
      });

      source = audioContext.createMediaStreamSource(stream);

      // Create Blob URL for the AudioWorklet processor
      const blob = new Blob([AUDIO_PROCESSOR_CODE], { type: "application/javascript" });
      processorBlobUrl = URL.createObjectURL(blob);

      await audioContext.audioWorklet.addModule(processorBlobUrl);
      audioWorkletNode = new AudioWorkletNode(audioContext, "audio-processor");

      source.connect(audioWorkletNode);
      audioWorkletNode.connect(audioContext.destination);

      audioWorkletNode.port.onmessage = (event) => {
        if (!audioContext) return;

        const currentBuffer = new Int16Array(event.data.audio_data);
        audioBufferQueue = mergeBuffers(audioBufferQueue, currentBuffer);

        // Calculate buffer duration in milliseconds
        const bufferDuration =
          (audioBufferQueue.length / audioContext.sampleRate) * 1000;

        // Send audio in 100ms chunks
        if (bufferDuration >= 100) {
          const totalSamples = Math.floor(audioContext.sampleRate * 0.1);
          const finalBuffer = new Uint8Array(
            audioBufferQueue.subarray(0, totalSamples).buffer
          );
          audioBufferQueue = audioBufferQueue.subarray(totalSamples);

          if (onAudioCallback) {
            onAudioCallback(finalBuffer);
          }
        }
      };
    },

    stopRecording() {
      // Set abort flag to prevent any pending startRecording from creating new streams
      isAborted = true;

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      audioContext?.close();
      if (processorBlobUrl) {
        URL.revokeObjectURL(processorBlobUrl);
        processorBlobUrl = null;
      }
      audioBufferQueue = new Int16Array(0);
      stream = null;
      audioContext = null;
      audioWorkletNode = null;
      source = null;
    },

    getStream() {
      return stream;
    },
  };
}

/**
 * Custom hook for real-time speech-to-text dictation using AssemblyAI or Deepgram.
 *
 * Usage:
 *   const { isRecording, transcript, startDictation, stopDictation } = useDictation({
 *     onTranscript: (text, isFinal) => {
 *       if (isFinal) setPrompt(prev => prev + ' ' + text);
 *     },
 *     provider: 'deepgram', // default; pass 'assemblyai' to use AssemblyAI
 *   });
 */
export function useDictation({
  onTranscript,
  onError,
  provider = "deepgram",
  inactivityTimeout = 20000,
}: UseDictationOptions): UseDictationReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const microphoneRef = useRef<MicrophoneController | null>(null);
  const turnsRef = useRef<Record<number, string>>({});
  const isStoppingRef = useRef(false);
  // Track active media stream separately to ensure cleanup even if microphone ref is cleared
  const activeStreamRef = useRef<MediaStream | null>(null);
  // For Deepgram, track accumulated transcript
  const deepgramTranscriptRef = useRef("");
  // Idle timeout - stop if no transcript received within 20 seconds
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasReceivedTranscriptRef = useRef(false);

  const stopDictation = useCallback(() => {
    // Prevent multiple simultaneous stop calls
    if (isStoppingRef.current) {
      return;
    }
    isStoppingRef.current = true;

    try {
      // Clear idle timeout
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }

      if (wsRef.current) {
        try {
          if (wsRef.current.readyState === WebSocket.OPEN) {
            if (provider === "assemblyai") {
              wsRef.current.send(JSON.stringify({ type: "Terminate" }));
            }
            // Deepgram doesn't need a terminate message
          }
          wsRef.current.close();
        } catch (e) {
          // Ignore errors when closing
        }
        wsRef.current = null;
      }

      if (microphoneRef.current) {
        microphoneRef.current.stopRecording();
        microphoneRef.current = null;
      }

      // Also stop any orphaned streams that might have been created
      if (activeStreamRef.current) {
        activeStreamRef.current.getTracks().forEach((track) => track.stop());
        activeStreamRef.current = null;
      }

      setIsRecording(false);
      turnsRef.current = {};
      deepgramTranscriptRef.current = "";
      hasReceivedTranscriptRef.current = false;
    } finally {
      isStoppingRef.current = false;
    }
  }, [provider]);

  const startDictation = useCallback(async () => {
    if (isRecording) {
      stopDictation();
      return;
    }

    // Clear any existing idle timeout from a previous session
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }

    setIsLoading(true);
    setError(null);
    setTranscript("");
    turnsRef.current = {};
    deepgramTranscriptRef.current = "";
    hasReceivedTranscriptRef.current = false;

    try {
      // Request microphone permission first
      const microphone = createMicrophone();
      await microphone.requestPermission();
      microphoneRef.current = microphone;
      // Track stream immediately after permission is granted
      activeStreamRef.current = microphone.getStream();

      // Get transcription token from backend (supports both providers)
      const response = await axios.get(`${hostname}/public/transcription-token`, {
        params: { provider }
      });

      if (!response.data.token) {
        throw new Error("Failed to get transcription token");
      }

      const token = response.data.token;

      if (provider === "deepgram") {
        // Connect to Deepgram Flux WebSocket
        // Flux requires /v2/listen endpoint and has built-in turn detection
        // eot_threshold: confidence required for EndOfTurn (0.5-0.9, default 0.7)
        // eot_timeout_ms: max silence before forcing EndOfTurn (500-10000, default 5000)
        // Using lower threshold and shorter timeout for faster response
        const endpoint = `wss://api.deepgram.com/v2/listen?model=flux-general-en&encoding=linear16&sample_rate=16000&eot_threshold=0.5&eot_timeout_ms=1500`;
        const ws = new WebSocket(endpoint, ["token", token]);
        wsRef.current = ws;

        ws.onopen = async () => {
          setIsRecording(true);
          setIsLoading(false);

          // Start idle timeout - stop if no transcript received (0 = disabled)
          if (inactivityTimeout > 0) {
            idleTimeoutRef.current = setTimeout(() => {
              if (!hasReceivedTranscriptRef.current) {
                // Close WebSocket and cleanup
                if (wsRef.current) {
                  try {
                    wsRef.current.close();
                  } catch (e) {
                    // Ignore
                  }
                  wsRef.current = null;
                }
                if (microphoneRef.current) {
                  microphoneRef.current.stopRecording();
                  microphoneRef.current = null;
                }
                if (activeStreamRef.current) {
                  activeStreamRef.current.getTracks().forEach((track) => track.stop());
                  activeStreamRef.current = null;
                }
                setIsRecording(false);
                deepgramTranscriptRef.current = "";
                hasReceivedTranscriptRef.current = false;
                setError("Dictation stopped due to inactivity");
                onError?.("Dictation stopped due to inactivity");
              }
            }, inactivityTimeout);
          }

          // Start recording and send audio chunks
          await microphone.startRecording((audioChunk) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(audioChunk);
            }
          });
          // Track the stream for cleanup
          activeStreamRef.current = microphone.getStream();
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            const eventType = msg.event;

            // Flux events: Update, StartOfTurn, EagerEndOfTurn, TurnResumed, EndOfTurn
            if (eventType === "StartOfTurn") {
              // StartOfTurn means user started speaking - clear idle timeout immediately
              if (!hasReceivedTranscriptRef.current) {
                hasReceivedTranscriptRef.current = true;
                if (idleTimeoutRef.current) {
                  clearTimeout(idleTimeoutRef.current);
                  idleTimeoutRef.current = null;
                }
              }
              const transcriptText = msg.transcript || "";
              if (transcriptText) {
                deepgramTranscriptRef.current = transcriptText;
                setTranscript(transcriptText);
                onTranscript?.(transcriptText, false);
              }
            } else if (eventType === "Update") {
              // Interim transcripts
              const transcriptText = msg.transcript || "";

              if (transcriptText) {
                // Mark that we've received a transcript - clear idle timeout
                if (!hasReceivedTranscriptRef.current) {
                  hasReceivedTranscriptRef.current = true;
                  if (idleTimeoutRef.current) {
                    clearTimeout(idleTimeoutRef.current);
                    idleTimeoutRef.current = null;
                  }
                }

                deepgramTranscriptRef.current = transcriptText;
                setTranscript(transcriptText);
                onTranscript?.(transcriptText, false);
              }
            } else if (eventType === "EndOfTurn") {
              // End of turn detected - this is the final transcript for this turn
              const transcriptText = msg.transcript || deepgramTranscriptRef.current;
              if (transcriptText.trim()) {
                setTranscript(transcriptText);
                onTranscript?.(transcriptText.trim(), true);
                deepgramTranscriptRef.current = "";
              }
            }
            // Ignore TurnResumed and EagerEndOfTurn for now - we wait for EndOfTurn
          } catch (e) {
            console.error("Error parsing Deepgram message:", e);
          }
        };

        ws.onerror = (err) => {
          console.error("Deepgram WebSocket error:", err);
          const errorMsg = "Connection error. Please try again.";
          setError(errorMsg);
          onError?.(errorMsg);
          stopDictation();
        };

        ws.onclose = (event) => {
          console.log("Deepgram WebSocket closed:", event.code, event.reason);
          setIsRecording(false);
          setIsLoading(false);
        };
      } else {
        // AssemblyAI provider (default)
        // Connect to AssemblyAI WebSocket using v3 Universal-Streaming API
        // Turn detection settings:
        // - end_of_turn_confidence_threshold: confidence level to trigger end of turn (0-1, default: ~0.5)
        // - min_end_of_turn_silence_when_confident: minimum silence (ms) when confident turn ended (default: 400ms)
        // - max_turn_silence: maximum silence (ms) before forcing turn end (default: 2400ms, reduced to 950ms)
        // - inactivity_timeout: seconds of inactivity before session terminates (5-3600, default: none)
        const endpoint = `wss://streaming.assemblyai.com/v3/ws?sample_rate=16000&format_turns=true&end_of_turn_confidence_threshold=0.4&min_end_of_turn_silence_when_confident=400&max_turn_silence=950&inactivity_timeout=20&token=${token}`;
        const ws = new WebSocket(endpoint);
        wsRef.current = ws;

        ws.onopen = async () => {
          setIsRecording(true);
          setIsLoading(false);

          // Start idle timeout - stop if no transcript received (0 = disabled)
          if (inactivityTimeout > 0) {
            idleTimeoutRef.current = setTimeout(() => {
              if (!hasReceivedTranscriptRef.current) {
                // Close WebSocket and cleanup directly to avoid stale closure issues
                if (wsRef.current) {
                  try {
                    if (wsRef.current.readyState === WebSocket.OPEN) {
                      wsRef.current.send(JSON.stringify({ type: "Terminate" }));
                    }
                    wsRef.current.close();
                  } catch (e) {
                    // Ignore
                  }
                  wsRef.current = null;
                }
                if (microphoneRef.current) {
                  microphoneRef.current.stopRecording();
                  microphoneRef.current = null;
                }
                if (activeStreamRef.current) {
                  activeStreamRef.current.getTracks().forEach((track) => track.stop());
                  activeStreamRef.current = null;
                }
                setIsRecording(false);
                turnsRef.current = {};
                hasReceivedTranscriptRef.current = false;
                setError("Dictation stopped due to inactivity");
                onError?.("Dictation stopped due to inactivity");
              }
            }, inactivityTimeout);
          }

          // Start recording and send audio chunks
          await microphone.startRecording((audioChunk) => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(audioChunk);
            }
          });
          // Track the stream for cleanup
          activeStreamRef.current = microphone.getStream();
        };

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            const msgType = msg.type;

            if (msgType === "Turn") {
              // Turn contains transcript data - receiving a Turn means speech was detected
              const turnTranscript = msg.transcript || "";
              const turnOrder = msg.turn_order;
              const isFormatted = msg.turn_is_formatted;
              const endOfTurn = msg.end_of_turn === true;

              // Clear idle timeout as soon as we receive any Turn (speech detected)
              if (!hasReceivedTranscriptRef.current) {
                hasReceivedTranscriptRef.current = true;
                if (idleTimeoutRef.current) {
                  clearTimeout(idleTimeoutRef.current);
                  idleTimeoutRef.current = null;
                }
              }

              if (turnOrder !== undefined) {
                turnsRef.current[turnOrder] = turnTranscript;
              }

              // Combine all turns in order
              const orderedTurns = Object.keys(turnsRef.current)
                .sort((a, b) => Number(a) - Number(b))
                .map((k) => turnsRef.current[Number(k)])
                .join(" ")
                .trim();

              setTranscript(orderedTurns);
              // A turn is only final once end_of_turn is true AND it's been formatted.
              // AssemblyAI sends interim formatted turns with end_of_turn=false; treating
              // those as final caused partial transcripts to be appended repeatedly.
              onTranscript?.(orderedTurns, endOfTurn && isFormatted === true);
            }
          } catch (e) {
            console.error("Error parsing WebSocket message:", e);
          }
        };

        ws.onerror = () => {
          const errorMsg = "Connection error. Please try again.";
          setError(errorMsg);
          onError?.(errorMsg);
          stopDictation();
        };

        ws.onclose = () => {
          setIsRecording(false);
          setIsLoading(false);
        };
      }
    } catch (err: any) {
      const errorMsg =
        err.message || "Failed to start dictation. Check microphone permissions.";
      setError(errorMsg);
      onError?.(errorMsg);
      setIsLoading(false);
      stopDictation();
    }
  }, [isRecording, onTranscript, onError, stopDictation, provider]);

  return {
    isRecording,
    isLoading,
    transcript,
    startDictation,
    stopDictation,
    error,
  };
}
